// DE: Datumsfunktionen auf ISO-Strings (YYYY-MM-DD) in UTC, damit Zeitzonen keine Tage verschieben.
// EN: Date helpers on ISO strings (YYYY-MM-DD) in UTC so that time zones never shift days.

export type IsoDate = string;

/**
 * DE: ISO-Wochentag: Montag = 1 bis Sonntag = 7.
 * EN: ISO weekday: Monday = 1 to Sunday = 7.
 */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface Period {
  start: IsoDate;
  days: number;
}

const MS_PER_DAY = 86_400_000;

function toTime(date: IsoDate): number {
  return Date.parse(`${date}T00:00:00Z`);
}

function fromTime(time: number): IsoDate {
  return new Date(time).toISOString().slice(0, 10);
}

/**
 * DE: Datum um `days` Tage verschieben (auch negativ).
 * EN: Shift a date by `days` days (negative values allowed).
 */
export function addDays(date: IsoDate, days: number): IsoDate {
  return fromTime(toTime(date) + days * MS_PER_DAY);
}

/**
 * DE: ISO-Wochentag: Montag = 1, Sonntag = 7.
 * EN: ISO weekday: Monday = 1, Sunday = 7.
 */
export function weekday(date: IsoDate): Weekday {
  const day = new Date(toTime(date)).getUTCDay();
  return (day === 0 ? 7 : day) as Weekday;
}

/**
 * DE: Anzahl Tage von `from` bis `to` (negativ, wenn `to` früher liegt).
 * EN: Number of days from `from` to `to` (negative when `to` is earlier).
 */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((toTime(to) - toTime(from)) / MS_PER_DAY);
}

/**
 * DE: Anzahl Tage eines Monats (Monat 1–12).
 * EN: Number of days in a month (month 1–12).
 */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * DE: ISO-Datum aus Jahr, Monat (1–12) und Tag.
 * EN: ISO date from year, month (1–12) and day.
 */
export function isoDate(year: number, month: number, day: number): IsoDate {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * DE: Zeitraum eines ganzen Kalendermonats.
 * EN: Period covering a whole calendar month.
 */
export function monthPeriod(year: number, month: number): Period {
  return { start: isoDate(year, month, 1), days: daysInMonth(year, month) };
}

/**
 * DE: Zeitraum einer Woche ab Montag; andere Starttage sind ein Fehler.
 * EN: Period of one week starting on Monday; other start days are an error.
 */
export function weekPeriod(monday: IsoDate): Period {
  if (weekday(monday) !== 1) throw new RangeError(`${monday} is not a Monday`);
  return { start: monday, days: 7 };
}

/**
 * DE: Alle Tage eines Zeitraums in Reihenfolge.
 * EN: All dates of a period in order.
 */
export function datesOf(period: Period): IsoDate[] {
  return Array.from({ length: period.days }, (_, index) => addDays(period.start, index));
}

/**
 * DE: Montag der ISO-Woche, in der das Datum liegt.
 * EN: Monday of the ISO week that contains the date.
 */
export function isoWeekStart(date: IsoDate): IsoDate {
  return addDays(date, 1 - weekday(date));
}
